import {
  AfterContentInit,
  ContentChild,
  Directive,
  ElementRef,
  OnDestroy,
  inject,
} from '@angular/core';
import { TabList } from 'primeng/tabs';

/**
 * O PrimeNG só recalcula a posição do inkbar quando o tamanho do `p-tabs`
 * muda (ResizeObserver interno). Se o `p-tabs` estiver dentro de um
 * p-dialog/bottom-sheet ainda animando/oculto no momento da primeira medição,
 * a posição sai errada e nunca é corrigida (o container não muda de tamanho,
 * só anima via CSS transform/opacity).
 *
 * Corrigimos observando o próprio `p-tabs` e o fim de qualquer transição CSS
 * ao redor, forçando `updateInkBar()` sem a transição padrão do PrimeNG
 * (.p-tablist-active-bar) para não gerar um "slide" visível ao corrigir.
 */
@Directive({
  selector: 'p-tabs[libTabsInkbarFix]',
  standalone: true,
})
export class TabsInkbarFixDirective implements AfterContentInit, OnDestroy {
  private elementRef = inject(ElementRef<HTMLElement>);

  @ContentChild(TabList) private tabList?: TabList;

  private resizeObserver?: ResizeObserver;
  private onExternalChange = () => this.snapInkBar();

  ngAfterContentInit(): void {
    this.snapInkBar();

    this.resizeObserver = new ResizeObserver(() => this.snapInkBar());
    this.resizeObserver.observe(this.elementRef.nativeElement);

    document.addEventListener('transitionend', this.onExternalChange, true);
    document.addEventListener('animationend', this.onExternalChange, true);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    document.removeEventListener('transitionend', this.onExternalChange, true);
    document.removeEventListener('animationend', this.onExternalChange, true);
  }

  private snapInkBar(): void {
    const inkbarEl = this.tabList?.inkbar?.nativeElement;
    if (!inkbarEl) {
      this.tabList?.updateInkBar();
      return;
    }

    inkbarEl.style.transition = 'none';
    this.tabList?.updateInkBar();
    void inkbarEl.offsetWidth;
    inkbarEl.style.transition = '';
  }
}
